import { IMenuDomain } from '../index';
import IconMi from 'components/common/icon/IconMi';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import IconHandler from 'components/common/icon/IconHandler';
import { merge } from 'lodash';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from '../styles';
import { useSelector } from 'react-redux';
import useBundleTranslation from 'i18n';

interface IProps {
    domain: IMenuDomain;
    onSelectPrompt: (domain: string, prompt: string) => void;
    needsAuth: boolean;
    closeMenu: () => void;
    onAuthClick?: () => void;
    onAuthPromptClick?: (prompt: string) => void;
}

export default function ExtraMenu({
    domain,
    onSelectPrompt,
    needsAuth,
    closeMenu,
    onAuthClick,
    onAuthPromptClick,
}: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);

    const [hover, setHover] = useState(false);
    const hideTimerRef = useRef<number | null>(null);
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [menuPos, setMenuPos] = useState<{ top?: number; bottom?: number }>({});

    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;

    const styles = useMemo(() => {
        return merge({}, defaultStyles, customStyles?.footerDomainSelector) as typeof defaultStyles;
    }, [customStyles]);

    const handleEnter = () => {
        if (hideTimerRef.current) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
        setHover(true);
    };

    const handleLeave = () => {
        if (hideTimerRef.current) {
            window.clearTimeout(hideTimerRef.current);
        }
        hideTimerRef.current = window.setTimeout(() => {
            setHover(false);
            hideTimerRef.current = null;
        }, 500);
    };

    useEffect(() => {
        if (!hover) return;
        const compute = () => {
            const triggerEl = triggerRef.current;
            const menuEl = menuRef.current;
            if (!triggerEl || !menuEl) return;

            const prevDisplay = menuEl.style.display;
            if (getComputedStyle(menuEl).display === 'none') {
                menuEl.style.display = 'block';
            }

            const triggerRect = triggerEl.getBoundingClientRect();
            const menuRect = menuEl.getBoundingClientRect();
            const offsetParent = menuEl.offsetParent as HTMLElement | null;
            const parentRect = offsetParent
                ? offsetParent.getBoundingClientRect()
                : ({ top: 0, height: window.innerHeight } as any);

            let desiredTop = triggerRect.top + triggerRect.height / 2 - menuRect.height / 2 - parentRect.top;
            if (desiredTop < 0) desiredTop = 0;

            const wouldOverflowBottom = desiredTop + menuRect.height > parentRect.height;

            if (wouldOverflowBottom) {
                setMenuPos({ top: undefined, bottom: 0 });
            } else {
                setMenuPos({ top: Math.round(desiredTop), bottom: undefined });
            }

            menuEl.style.display = prevDisplay;
        };

        const raf = window.requestAnimationFrame(compute);
        return () => window.cancelAnimationFrame(raf);
    }, [hover]);

    useEffect(() => {
        return () => {
            if (hideTimerRef.current) {
                window.clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
        };
    }, []);

    const iconProps = {
        type: domain.icon_ind,
        value: domain.icon_ind === 'upload' ? `data:image/png;base64,${domain.icon_data}` : domain.icon_name,
    };

    const showExpandMenu = domain.description || (domain.suggestedPrompts && domain.suggestedPrompts.length > 0);

    const handleAuthClick = (event: React.MouseEvent) => {
        (event as any).__miCloseDropdown = true;
        event.preventDefault();
        event.stopPropagation();
        onAuthClick?.();
        closeMenu();
    };

    const handleAuthPromptClick = (event: React.MouseEvent, prompt: string) => {
        (event as any).__miCloseDropdown = true;
        event.preventDefault();
        event.stopPropagation();
        onAuthPromptClick?.(prompt);
        closeMenu();
    };

    const onSelectExample = (event: React.MouseEvent, prompt: string) => {
        (event as any).__miCloseDropdown = true;
        event.preventDefault();
        event.stopPropagation();
        onSelectPrompt(domain.name, prompt);
        closeMenu();
    };

    if (needsAuth) {
        if (!showExpandMenu) {
            return (
                <Box>
                    <Tooltip title={t('footer.auth_required')} arrow placement="top">
                        <Box
                            ref={triggerRef}
                            width={'21px'}
                            height={'21px'}
                            display="flex"
                            justifyContent={'center'}
                            alignItems={'center'}
                        >
                            <IconMi icon={'key'} fontSize="16" />
                        </Box>
                    </Tooltip>
                </Box>
            );
        }

        return (
            <Stack onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
                <Box>
                    <Box
                        ref={triggerRef}
                        width={'21px'}
                        height={'21px'}
                        display="flex"
                        justifyContent={'center'}
                        alignItems={'center'}
                    >
                        <IconMi icon={'key'} fontSize="16" />
                    </Box>
                </Box>
                <Stack
                    ref={menuRef}
                    sx={{
                        ...styles.extraMenu,
                        top: menuPos.top !== undefined ? `${menuPos.top}px` : 'auto',
                        bottom: menuPos.bottom !== undefined ? `${menuPos.bottom}px` : 'auto',
                    }}
                    display={hover ? 'block' : 'none'}
                    spacing={1}
                >
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        spacing={1}
                        justifyContent={'flex-start'}
                        onClick={handleAuthClick}
                        sx={{
                            cursor: 'pointer',
                            p: 1,
                            mb: 1,
                            backgroundColor: 'action.hover',
                            borderRadius: 1,
                            '&:hover': {
                                backgroundColor: 'action.selected',
                            },
                        }}
                    >
                        <IconMi icon={'key'} fontSize="16" />
                        <Typography fontWeight={600}>{t('footer.auth_required')}</Typography>
                    </Stack>
                    <Stack direction={'row'} alignItems={'center'} spacing={1} justifyContent={'flex-start'}>
                        <IconHandler icon={iconProps} sx={{ fontSize: '16px' }} />
                        <Typography fontWeight={600}>{domain.title}</Typography>
                    </Stack>
                    <Box>{domain.description && <Typography>{domain.description}</Typography>}</Box>
                    {domain.suggestedPrompts && domain.suggestedPrompts.length > 0 && (
                        <Stack sx={styles.examplesList} spacing={0.5}>
                            {domain.suggestedPrompts.map((prompt, index) => {
                                return (
                                    <Stack
                                        key={index}
                                        direction={'row'}
                                        sx={styles.examplesItem}
                                        onClick={(e) => handleAuthPromptClick(e, prompt)}
                                    >
                                        <Box flexGrow={1}>{prompt}</Box>
                                        <Box sx={styles.examplesItemIcon}>
                                            <IconMi icon="arrow-right" sx={{ fontSize: '16px' }} />
                                        </Box>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    )}
                </Stack>
            </Stack>
        );
    }

    return showExpandMenu ? (
        <Stack onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
            <Box>
                <Box
                    ref={triggerRef}
                    width={'21px'}
                    height={'21px'}
                    display="flex"
                    justifyContent={'center'}
                    alignItems={'center'}
                >
                    <IconMi icon={'info-circle'} fontSize="16" />
                </Box>
            </Box>
            <Stack
                ref={menuRef}
                sx={{
                    ...styles.extraMenu,
                    top: menuPos.top !== undefined ? `${menuPos.top}px` : 'auto',
                    bottom: menuPos.bottom !== undefined ? `${menuPos.bottom}px` : 'auto',
                }}
                display={hover ? 'block' : 'none'}
                spacing={1}
            >
                <Stack direction={'row'} alignItems={'center'} spacing={1} justifyContent={'flex-start'}>
                    <IconHandler icon={iconProps} sx={{ fontSize: '16px' }} />
                    <Typography fontWeight={600}>{domain.title}</Typography>
                </Stack>
                <Box>{domain.description && <Typography>{domain.description}</Typography>}</Box>
                {domain.suggestedPrompts && domain.suggestedPrompts.length > 0 && (
                    <Stack sx={styles.examplesList} spacing={0.5}>
                        {domain.suggestedPrompts.map((prompt, index) => {
                            return (
                                <Stack
                                    key={index}
                                    direction={'row'}
                                    sx={styles.examplesItem}
                                    onClick={(e) => onSelectExample(e, prompt)}
                                >
                                    <Box flexGrow={1}>{prompt}</Box>
                                    <Box sx={styles.examplesItemIcon}>
                                        <IconMi icon="arrow-right" sx={{ fontSize: '16px' }} />
                                    </Box>
                                </Stack>
                            );
                        })}
                    </Stack>
                )}
            </Stack>
        </Stack>
    ) : null;
}
